import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, Send } from "lucide-react";

const SUGGESTIONS = [
  "What can I study with an APS of 30?",
  "How do I apply for NSFAS?",
  "What's the difference between a university and a TVET college?",
  "How much does it cost to apply?",
];

export default function AiHelp() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem("yva_ai_sid") || "");
  const endRef = useRef(null);

  useEffect(() => {
    if (sid) api.get("/ai/history", { params: { session_id: sid } }).then(({ data }) => setMessages(data.messages)).catch(() => {});
  }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sid || null });
      if (!sid) { setSid(data.session_id); localStorage.setItem("yva_ai_sid", data.session_id); }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't answer just now. Please try again." }]);
    } finally { setSending(false); }
  };

  const reset = () => { localStorage.removeItem("yva_ai_sid"); setSid(""); setMessages([]); };

  return (
    <div className="min-h-screen bg-[#F8F9FA]"><Header />
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><span className="w-9 h-9 rounded-xl bg-[#0B2447] flex items-center justify-center"><Sparkles className="w-5 h-5 text-[#F5C32C]" /></span>
            <div><h1 className="font-display text-2xl font-bold text-[#0B2447]" data-testid="ai-title">Varsty Assist</h1><p className="text-xs text-slate-500">Your AI study &amp; application helper</p></div>
          </div>
          {messages.length > 0 && <Button variant="ghost" size="sm" onClick={reset} data-testid="ai-reset">New chat</Button>}
        </div>

        <div className="mt-5 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col h-[62vh]" data-testid="ai-chat">
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <p className="text-slate-500">Ask me anything about studying, applications, APS or NSFAS.</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s)} data-testid={`ai-suggest-${i}`} className="text-left text-sm border border-slate-200 rounded-lg px-3 py-2.5 text-[#0B2447] hover:bg-slate-50">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}-${i}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#0B2447] text-white" : "bg-slate-100 text-slate-800"}`}>{m.content}</div>
              </div>
            ))}
            {sending && <div className="flex justify-start"><div className="bg-slate-100 rounded-2xl px-4 py-2.5"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div></div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-slate-100 p-3 flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question…" data-testid="ai-input" className="flex-1" />
            <Button type="submit" disabled={sending || !input.trim()} data-testid="ai-send" className="bg-[#0B2447] hover:bg-[#153461]"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
        <p className="text-xs text-slate-400 mt-2 text-center">Varsty Assist can make mistakes — always confirm important details on official sites.</p>
      </div>
    </div>
  );
}
